import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown, Calculator } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleMobileMenuClick = () => {
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-effect border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="/logo.svg"
               alt="Star Nivesh" 
              className="h-[50px] w-auto"
            />
          </Link>

          <Link
            to="https://www.amfiindia.com/locate-distributor" 
            className="text-text-muted text-xs hover:text-primary transition-colors border-l border-white/10 ml-6 pl-6"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-amfi-reg"
          >
            AMFI Registration Number: <span className="text-primary">290244</span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-text-primary hover:text-primary transition-colors" data-testid="nav-home">
              Home
            </Link>
            
            {/* Services Dropdown - includes Our Approach */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="text-text-primary hover:text-primary transition-colors flex items-center" data-testid="nav-services">
                  Services <ChevronDown className="ml-1 h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#1a1f2e] border-2 border-white/20 shadow-xl rounded-lg min-w-[200px]">
                <DropdownMenuItem asChild>
                  <Link to="/philosophy" className="text-text-primary hover:text-primary hover:bg-white/10 cursor-pointer block px-4 py-2" data-testid="nav-our-approach">Our Philosophy</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem asChild>
                  <Link to="/services/retail" className="text-text-primary hover:text-primary hover:bg-white/10 cursor-pointer block px-4 py-2" data-testid="nav-services-retail">Retail Investors</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/services/corporate" className="text-text-primary hover:text-primary hover:bg-white/10 cursor-pointer block px-4 py-2" data-testid="nav-services-corporate">Corporate Clients</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Calculators Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="text-text-primary hover:text-primary transition-colors flex items-center" data-testid="nav-calculators">
                  Calculators <ChevronDown className="ml-1 h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#1a1f2e] border-2 border-white/20 shadow-xl rounded-lg min-w-[200px]">
                <DropdownMenuItem asChild>
                  <Link to="/calculators/sip" className="text-text-primary hover:text-primary hover:bg-white/10 cursor-pointer block px-4 py-2" data-testid="nav-calc-sip">SIP Calculator</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/calculators/swp" className="text-text-primary hover:text-primary hover:bg-white/10 cursor-pointer block px-4 py-2" data-testid="nav-calc-swp">SWP Calculator</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/calculators/retirement" className="text-text-primary hover:text-primary hover:bg-white/10 cursor-pointer block px-4 py-2" data-testid="nav-calc-retirement">Retirement Calculator</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/calculators/fire" className="text-text-primary hover:text-primary hover:bg-white/10 cursor-pointer block px-4 py-2" data-testid="nav-calc-fire">FIRE Calculator</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/calculators/goal" className="text-text-primary hover:text-primary hover:bg-white/10 cursor-pointer block px-4 py-2" data-testid="nav-calc-goal">Goal Planner</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/calculators/emi" className="text-text-primary hover:text-primary hover:bg-white/10 cursor-pointer block px-4 py-2" data-testid="nav-calc-emi">EMI Calculator</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/blogs" className="text-text-primary hover:text-primary transition-colors" data-testid="nav-blogs">
              Blogs
            </Link>
            <Link to="/book-summaries" className="text-text-primary hover:text-primary transition-colors" data-testid="nav-book-summaries">
              Book Summaries
            </Link>
            <Link to="/books" className="text-text-primary hover:text-primary transition-colors" data-testid="nav-books">
              Books
            </Link>
            <Link to="/contact">
              <Button className="btn-primary" data-testid="nav-contact">Contact Us</Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-text-primary"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden pb-6 space-y-4" data-testid="mobile-menu">
            <Link to="/" className="block text-text-primary hover:text-primary transition-colors" onClick={handleMobileMenuClick}>Home</Link>
            
            {/* Services Section */}
            <div className="text-text-muted text-sm font-semibold mt-4">Services</div>
            <Link to="/philosophy" className="block text-text-primary hover:text-primary transition-colors pl-4" onClick={handleMobileMenuClick}>Our Philosophy</Link>
            <Link to="/services/retail" className="block text-text-primary hover:text-primary transition-colors pl-4" onClick={handleMobileMenuClick}>Retail Investors</Link>
            <Link to="/services/corporate" className="block text-text-primary hover:text-primary transition-colors pl-4" onClick={handleMobileMenuClick}>Corporate Clients</Link>
            
            {/* Calculators Section */}
            <div className="text-text-muted text-sm font-semibold mt-4">Calculators</div>
            <Link to="/calculators/sip" className="block text-text-primary hover:text-primary transition-colors pl-4" onClick={handleMobileMenuClick}>SIP Calculator</Link>
            <Link to="/calculators/swp" className="block text-text-primary hover:text-primary transition-colors pl-4" onClick={handleMobileMenuClick}>SWP Calculator</Link>
            <Link to="/calculators/retirement" className="block text-text-primary hover:text-primary transition-colors pl-4" onClick={handleMobileMenuClick}>Retirement Calculator</Link>
            <Link to="/calculators/fire" className="block text-text-primary hover:text-primary transition-colors pl-4" onClick={handleMobileMenuClick}>FIRE Calculator</Link>
            <Link to="/calculators/goal" className="block text-text-primary hover:text-primary transition-colors pl-4" onClick={handleMobileMenuClick}>Goal Planner</Link>
            <Link to="/calculators/emi" className="block text-text-primary hover:text-primary transition-colors pl-4" onClick={handleMobileMenuClick}>EMI Calculator</Link>
            
            <Link to="/blogs" className="block text-text-primary hover:text-primary transition-colors" onClick={handleMobileMenuClick}>Blogs</Link>
            <Link to="/book-summaries" className="block text-text-primary hover:text-primary transition-colors" onClick={handleMobileMenuClick}>Book Summaries</Link>
            <Link to="/books" className="block text-text-primary hover:text-primary transition-colors" onClick={handleMobileMenuClick}>Books</Link>
            <Link to="/contact" className="block" onClick={handleMobileMenuClick}>
              <Button className="btn-primary w-full">Contact Us</Button>
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
}
